<template>
  <v-container fluid>
    <div v-if="client.name">
      <!-- Title -->
      <Title
          v-if="client.name"
          :title="client.name"
          description="Consulta para generar nueva tabla de cantidades"
      />

      <!-- Consultation -->
      <v-row>
        <!-- Return button -->
        <v-col cols="12">
          <ReturnButton/>
        </v-col>
        <!-- Client Record -->
        <v-col cols="12" lg="7">
          <v-card class="fill-height">
            <v-card-title>Expediente del cliente</v-card-title>

            <v-divider></v-divider>

            <!-- Previous Quantity Table -->
            <v-card-subtitle>
              Tabla de cantidades anterior
            </v-card-subtitle>

            <v-card-text>
              <QuantityTableComponent
                  v-if="previousQuantityTable"
                  :quantityTable="previousQuantityTable"
                  :clientInfo="clientInformation"
              />
              <v-alert
                  type="info"
                  color="primary"
                  outlined
                  border="left"
                  class="mx-4"
                  v-else
              >
                No hay tabla de cantidades previa para este cliente.
              </v-alert>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Ailments -->
            <v-card-subtitle>Padecimientos</v-card-subtitle>
            <v-card-text>
              <v-row v-if="client.ailments?.length > 0">
                <v-col
                    cols="12"
                    lg="4"
                    v-for="(ailment, index) in client.ailments"
                    :key="index"
                >
                  <v-card
                      class="fill-height"
                      color="green lighten-4"
                  >
                    <v-card-title class="text-break">
                      {{ ailment.name }}
                    </v-card-title>
                    <v-card-text>
                      {{ ailment.description }}
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
              <v-alert
                  type="info"
                  color="primary"
                  outlined
                  border="left"
                  class="mx-4"
                  v-else
              >
                No hay padecimientos registrados para este cliente.
              </v-alert>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Additional Notes -->
            <v-card-subtitle>
              Notas adicionales
            </v-card-subtitle>

            <v-card-text>
              <v-textarea
                  outlined
                  dense
                  :loading="isLoadingClientNotes"
                  :disabled="isLoading"
                  label="Escribe aquí para editar..."
                  v-model="client.notes"
                  @input="onNotesInput"
                  rows="4"
                  counter
                  maxlength="255"
                  no-resize
              />
            </v-card-text>
          </v-card>
        </v-col>

        <!-- New Quantity Table -->
        <v-col cols="12" lg="5">
          <v-card>
            <v-card-title>
              Consulta de Nivel
              <v-chip
                  class="ml-2"
                  color="primary"
                  text-color="white"
                  label
                  small
              >
                {{ nextLevel }}{{ nextClass }}
              </v-chip>
              <v-chip
                  v-if="isTransitionTable && consultationWeightKg"
                  class="ml-2"
                  color="blue"
                  text-color="white"
                  label
                  small
              >
                Nivel de transición
              </v-chip>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-subtitle>
              Datos de la consulta y nueva tabla de cantidades
            </v-card-subtitle>

            <v-card-text>
              <v-form ref="consultationForm" :disabled="isLoading">
                <v-row>
                  <v-col cols="5">
                    <v-text-field
                        label="Peso en consulta (kg)"
                        type="number"
                        v-model.number="consultationWeightKg"
                        outlined
                        dense
                        prepend-inner-icon="mdi-weight"
                        :rules="[rules.required, rules.kilogram]"
                        min="30"
                        max="400"
                        placeholder="80.0"
                        hide-spin-buttons
                        suffix="kg"
                        @keydown="preventNonNumeric($event)"
                    />
                  </v-col>
                  <v-col cols="7" class="d-flex flex-column">
                    <span class="subtitle-1 font-weight-medium">
                    Peso anterior: {{ client.currentWeightKg.toFixed(3) }} kg
                    </span>
                    <span
                        class="subtitle-1
                        font-weight-medium"
                        :class="weightLossDisplayColor"
                        v-if="consultationWeightKg && consultationWeightKg > 25"
                    >
                    {{ weightLossDisplayText }}
                  </span>
                    <span
                        class="subtitle-1 font-weight-medium"
                        v-if="consultationWeightKg && consultationWeightKg > 25"
                    >
                    IMC actual: {{ BMI.toFixed(1) }} {{ BMILevel }}
                  </span>
                  </v-col>

                  <v-col cols="12" v-if="previousQuantityTable">
                    <v-checkbox
                        label="Comió recientemente"
                        v-model="ateRecently"
                        dense
                        hide-details
                    />
                    <div v-if="ateRecently">
                      <v-radio-group
                          v-model="foodRemovalTime"
                          :disabled="!ateRecently"
                          dense
                          row
                      >
                        <v-radio
                            label="Desayuno"
                            value="BREAKFAST"
                            color="green"
                        />
                        <v-radio
                            v-if="previousQuantityTable.morningSnack"
                            label="Snack de media mañana"
                            value="MORNING_SNACK"
                            color="green"
                        />
                        <v-radio
                            label="Comida"
                            value="LUNCH"
                            color="green"
                        />
                        <v-radio
                            v-if="previousQuantityTable.eveningSnack"
                            label="Snack de media tarde"
                            value="EVENING_SNACK"
                            color="green"
                        />
                        <v-radio
                            label="Cena"
                            value="DINNER"
                            color="green"
                        />
                      </v-radio-group>
                    </div>
                  </v-col>
                  <v-col cols="12">
                    <v-checkbox
                        label="Bebió líquidos recientemente"
                        v-model="drankRecently"
                        dense
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-select
                        label="Tipo de tabla de cantidades"
                        :items="tableTypes"
                        v-model="newTableType"
                        outlined
                        dense
                        :rules="[rules.required]"
                        :disabled="!consultationWeightKg"
                        no-data-text="No hay tipos de tabla disponibles"
                    />
                  </v-col>
                  <v-col cols="12" lg="6">
                    <v-select
                        label="Snack de media mañana (g)"
                        :items="snackOptions"
                        v-model="morningSnackGrams"
                        outlined
                        dense
                        no-data-text="No hay opciones disponibles"
                        hide-details
                    />
                  </v-col>
                  <v-col cols="12" lg="6">
                    <v-select
                        label="Snack de media tarde (g)"
                        :items="snackOptions"
                        v-model="eveningSnackGrams"
                        outlined
                        dense
                        no-data-text="No hay opciones disponibles"
                        hide-details
                    />
                  </v-col>
                  <v-col cols="12" v-if="nextLevel > 3">
                    <v-alert
                        type="info"
                        color="green"
                        outlined
                        dense
                        border="left"
                        class="mb-0"
                    >
                      A partir del nivel 4, considera reducir los snacks para evitar que el cliente consuma muy poco
                      alimento.
                    </v-alert>
                  </v-col>
                  <v-col cols="12">
                    <v-checkbox
                        label="Restar alimento"
                        v-model="decreaseFoodGrams"
                        dense
                        @input="updateConsultationWeight"
                        hide-details
                    />
                    <div v-if="decreaseFoodGrams">
                      <v-slider
                          label="Gramos a restar"
                          v-model="foodGramsToDecrease"
                          min="25"
                          max="100"
                          :step="25"
                          filled
                          dense
                          thumb-label
                          ticks="always"
                          tick-size="4"
                          :tick-labels="['25 g', '50 g', '75 g', '100 g']"
                      />
                    </div>
                  </v-col>

                  <v-col cols="12" lg="7">
                    <v-menu
                        v-if="!isMaintenanceTable"
                        v-model="menu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            :value="formattedAppointmentDateTime"
                            label="Próxima cita"
                            prepend-inner-icon="mdi-calendar-clock-outline"
                            :disabled="!newTableType"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            dense
                            clearable
                            @click:clear="selectedDate = null; selectedTime = null"
                            :rules="[rules.required]"
                        />
                      </template>

                      <v-card>
                        <v-tabs
                            v-model="activeTab"
                            background-color="primary"
                        >
                          <v-tab>
                            Fecha
                          </v-tab>
                          <v-tab :disabled="!selectedDate">
                            Hora
                          </v-tab>

                          <v-tab-item>
                            <v-date-picker
                                v-model="selectedDate"
                                first-day-of-week="1"
                                locale="es-MX"
                                :min="minimumDate"
                                :max="maximumDate"
                                scrollable
                                @input="activeTab = 1"
                            />
                          </v-tab-item>

                          <v-tab-item>
                            <v-time-picker
                                v-model="selectedTime"
                                scrollable
                            />
                          </v-tab-item>
                        </v-tabs>

                        <v-card-actions>
                          <v-spacer></v-spacer>
                          <v-btn
                              text
                              small
                              color="primary"
                              @click="menu = false; activeTab = 0"
                              :disabled="!selectedDate || !selectedTime"
                          >
                            Aceptar
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-menu>
                  </v-col>

                  <v-col cols="12">
                    <small class="grey--text" v-if="selectedTableTypeLabel">
                      La {{ selectedTableTypeLabel }} tiene un costo de ${{ selectedTableTypePrice.toFixed(2) }} y se te
                      descontarán ${{ (selectedTableTypePrice / 2).toFixed(2) }} de tu saldo para poder
                      generarla. Asegúrate de introducir los datos correctamente antes de hacerlo.
                    </small>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <CancelButton
                  :isDisabled="isLoading"
                  @click="$router.back()"
              />
              <v-btn :loading="isLoading" :disabled="isValidForm" color="primary" @click="createNewQuantityTable">
                <v-icon left>mdi-check</v-icon>
                Generar tabla
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- New quantity table modal -->
      <DialogNewQuantityTable
          v-model="isNewQuantityTableDialogVisible"
          @printed="$router.push({ name: 'ClientsTable' })"
          :clientInfo="clientInformation"
          :quantityTableProp="newQuantityTable"
          :hasEatenLunchOrDinner="hasEatenLunchOrDinner"
      />
    </div>

    <!-- Skeleton loader -->
    <OverlayLoading v-else-if="isLoading"/>
  </v-container>
</template>

<script lang="ts">
import Vue from "vue";
import Swal from "sweetalert2";

import {QuantityTableInfo} from "@/services/quantityTables/entities/QuantityTableInfo";
import {ClientConsultationInfo} from "@/services/clients/entities/ClientConsultationInfo";
import {ClientTableInfo} from "@/services/clients/entities/ClientTableInfo";
import {NewQuantityTable} from "@/services/quantityTables/entities/NewQuantityTable";
import {QuantityTableTypeOption} from "@/services/quantityTables/entities/QuantityTableTypeOption";

import throwSuccessMessage from "@/utils/SuccessMessage";
import {formatDateToISO, snackCalculator} from "@/utils/UniversalFunctions";

import ClientService from "@/services/clients/ClientService";
import QuantityTableServices from "@/services/quantityTables/QuantityTableServices";

import Title from "@/components/Title.vue";
import OverlayLoading from "@/components/OverlayLoading.vue";
import QuantityTableComponent from "@/components/QuantityTableComponent.vue";
import DialogNewQuantityTable from "@/views/clients/components/DialogNewQuantityTable.vue";
import CancelButton from "@/components/buttons/CancelButton.vue";
import SaveButton from "@/components/buttons/SaveButton.vue";
import ReturnButton from "@/components/buttons/ReturnButton.vue";
import throwErrorMessage from "@/utils/ErrorMessage";
import Pt from "vuetify/src/locale/pt";

export default Vue.extend({
  name: "ClientConsultation",
  components: {
    ReturnButton,
    SaveButton,
    CancelButton,
    DialogNewQuantityTable,
    Title,
    OverlayLoading,
    QuantityTableComponent,
  },
  data() {
    return {
      // Client
      client: {} as ClientConsultationInfo,
      consultationWeightKg: 0 as number,
      previousQuantityTable: {} as QuantityTableInfo,
      ateRecently: false as boolean,
      drankRecently: false as boolean,

      // Table
      snackOptions: [
        {text: 'Sin snack', value: 0},
        {text: '25 g', value: 25},
        {text: '50 g', value: 50},
        {text: '75 g', value: 75},
        {text: '100 g', value: 100},
        {text: '150 g', value: 150},
      ],
      newTableType: '' as string,
      foodRemovalTime: '' as string,
      morningSnackGrams: 0 as number,
      eveningSnackGrams: 0 as number,
      decreaseFoodGrams: false as boolean,
      foodGramsToDecrease: 0 as number,
      isNewQuantityTableDialogVisible: false as boolean,
      newQuantityTable: {} as QuantityTableInfo,
      originalTableTypes: [] as QuantityTableTypeOption[],

      // Next appointment
      menu: false as boolean,
      activeTab: 0 as number,
      selectedTime: null as string | null,
      selectedDate: null as string | null,

      // Notes
      debounceTimer: null as ReturnType<typeof setTimeout> | null,

      // Validation
      rules: {
        required: (value: any) => !!value || 'Campo requerido.',
        kilogram: (value: string) => {
          const isThreeDecimals = /^\d+(\.\d{1,3})?$/.test(value);
          if (!isThreeDecimals) return 'Solo se permiten números con hasta tres decimales';
          const numericValue = parseInt(value, 10);
          return (numericValue >= 30 && numericValue <= 400) || 'Ingresa un valor entre 30 y 400';
        },
      },

      // Loading
      isLoading: false,
      isLoadingClientNotes: false,
    };
  },
  computed: {
    nextLevel(): number {
      if (!this.consultationWeightKg) return this.client.currentLevel + 1; // if weight is not entered, assume weight was lost and go to next level
      if (this.consultationWeightKg < 25) return this.client.currentLevel; // prevent message while typing weight
      if (this.isClassCFailed) return this.client.currentLevel + 1; // if weight was gained in class C, go to next level
      if (this.lostWeightKg >= 0) return this.client.currentLevel // if weight was gained, stay in the same level

      return this.client.currentLevel + 1; // if weight was lost, go to next level
    },
    nextClass(): string {
      if (!this.consultationWeightKg) return 'A';
      if (this.consultationWeightKg < 25) return this.client.currentClass; // prevent message while typing weight
      if (this.lostWeightKg < 0) return 'A'; // if weight was lost, go to class A
      const downgradeMap: { [key: string]: string } = {
        'A': 'B',
        'B': 'C',
      }
      const nextClass = downgradeMap[this.client.currentClass]; // if weight was gained, downgrade class
      return nextClass || 'A';
    },
    lostWeightKg(): number {
      if (!this.consultationWeightKg || !this.client.currentWeightKg) return 0;
      let lostWeight = this.consultationWeightKg - parseFloat(this.client.currentWeightKg.toFixed(3));
      if (this.ateRecently) {
        const isABC = this.previousQuantityTable.type === 'ABC';
        let lastIndex = this.previousQuantityTable.quantityTableDetails.length;
        switch (this.foodRemovalTime) {
          case 'BREAKFAST':
            if (isABC) {
              lostWeight -= (this.previousQuantityTable.quantityTableDetails[lastIndex - 1].breakfastGrams / 1000) +
                  (this.previousQuantityTable.quantityTableDetails[lastIndex - 2].breakfastGrams / 1000);
              break;
            } else {
              lostWeight -= this.previousQuantityTable.quantityTableDetails[lastIndex - 1].breakfastGrams / 1000;
            }
            break;
          case 'MORNING_SNACK':
            lostWeight -= this.previousQuantityTable.morningSnack / 1000;
            break;
          case 'LUNCH':
            if (isABC) {
              lostWeight -= (this.previousQuantityTable.quantityTableDetails[lastIndex - 1].lunchGrams / 1000) +
                  (this.previousQuantityTable.quantityTableDetails[lastIndex - 2].lunchGrams / 1000);
            } else {

              lostWeight -= this.previousQuantityTable.quantityTableDetails[lastIndex - 1].lunchGrams / 1000;
            }
            break;
          case 'EVENING_SNACK':
            lostWeight -= this.previousQuantityTable.eveningSnack / 1000;
            break;
          case 'DINNER':
            if (isABC) {
              lostWeight -= (this.previousQuantityTable.quantityTableDetails[lastIndex - 1].dinnerGrams / 1000) +
                  (this.previousQuantityTable.quantityTableDetails[lastIndex - 2].dinnerGrams / 1000);
              break;
            } else {
              lostWeight -= this.previousQuantityTable.quantityTableDetails[lastIndex - 1].dinnerGrams / 1000;
            }
            break;
        }
      }
      if (this.drankRecently) {
        lostWeight -= 0.3;
      }
      return lostWeight;
    },
    tableHeaderClass(): string {
      return 'red white--text';
    },
    weightLossDisplayText(): string {
      if (this.lostWeightKg === 0) return 'No hubo cambio de peso';
      const absWeight = Math.abs(this.lostWeightKg);
      const action = this.lostWeightKg > 0 ? 'Subió' : 'Bajó';
      if (absWeight < 1) {
        return `${action} ${(absWeight * 1000).toFixed(0)} g.`;
      }
      return `${action} ${absWeight.toFixed(3)} kg.`;
    },
    weightLossDisplayColor(): string {
      if (this.lostWeightKg < 0) {
        return 'green--text';
      } else {
        return 'red--text';
      }
    },
    BMILevel(): string {
      if (!this.BMI) return '';
      if (this.BMI < 18.5) return '(Bajo peso)';
      if (this.BMI >= 18.5 && this.BMI < 25) return '(Normal)';
      if (this.BMI >= 25 && this.BMI < 30) return '(Sobrepeso)';
      if (this.BMI >= 30 && this.BMI < 35) return '(Obesidad grado I)';
      if (this.BMI >= 35 && this.BMI < 40) return '(Obesidad grado II)';
      if (this.BMI >= 40) return '(Obesidad grado III)';
      return '';
    },
    BMI(): number {
      if (!this.consultationWeightKg) return 0;
      return (this.consultationWeightKg * 10000) / (this.client.heightCm * this.client.heightCm);
    },
    clientInformation(): ClientTableInfo {
      return {
        idClient: this.client.idClient,
        publicId: this.client.publicId,
        name: this.client.name,
        BMI: this.client.BMI,
        currentBreakfastPercentage: this.client.currentBreakfastPercentage,
        currentLunchPercentage: this.client.currentLunchPercentage,
        currentDinnerPercentage: this.client.currentDinnerPercentage,
        currentWeightKg: this.client.currentWeightKg,
        idealWeightKg: this.client.idealWeightKg,
        initialWeightKg: this.client.initialWeightKg,
        lostWeightKg: this.client.previousLostWeightKg,
      }
    },
    canGenerateABC(): boolean {
      // ABC cannot be generated if there's a transition level two or fewer levels ahead
      if (this.isTransitionTable) return false;
      const levelsToNextTransition = 8 - ((this.nextLevel - 1) % 8);
      return levelsToNextTransition > 2;
    },
    tableTypes(): Array<{ text: string; value: string }> {
      let tableTypeList = [
        {text: 'Tabla Semanal', value: 'WEEKLY'},
      ];
      // Add ABC, Vacation, and Extraordinary if level allows
      if (this.nextLevel >= 2) {
        tableTypeList.push(
            {text: 'Tabla de Vacaciones', value: 'VACATION'},
            {text: 'Tabla Extraordinaria', value: 'EXTRA'},
        );
        if (this.canGenerateABC) {
          tableTypeList.push({text: 'Tabla ABC', value: 'ABC'},)
        } else {
          // Remove ABC if exists
          tableTypeList = tableTypeList.filter(table => table.value !== 'ABC');
        }
      }

      // Add Control Table only if within 8 kg of ideal weight
      if (this.consultationWeightKg && this.consultationWeightKg <= this.client.idealWeightKg + 8) {
        tableTypeList.push({text: 'Tabla de Control', value: 'CONTROL'},)
      } else {
        // Remove Control Table if exists
        tableTypeList = tableTypeList.filter(table => table.value !== 'CONTROL');
      }
      // Add Maintenance Table only if meets the criteria
      const MAINTENANCE_AVAILABILITY_MATRIX: Record<string, Record<string, number>> = {
        MALE: {
          SMALL: 0,
          MEDIUM: 0.03,
          LARGE: 0.06,
        },
        FEMALE: {
          SMALL: 0,
          MEDIUM: 0.045,
          LARGE: 0.075,
        }
      }
      const maintenanceThreshold = MAINTENANCE_AVAILABILITY_MATRIX[this.client.gender][this.client.complexionType];
      const maxAllowedWeight = this.client.idealWeightKg * (1 + maintenanceThreshold);
      if (this.consultationWeightKg && this.consultationWeightKg <= maxAllowedWeight) {
        tableTypeList.push({text: 'Tabla de Mantenimiento', value: 'MAINTENANCE'},)
      } else {
        tableTypeList = tableTypeList.filter(table => table.value !== 'MAINTENANCE');
      }
      return tableTypeList;
    },
    selectedTableTypePrice(): number {
      // Weekly tables at level 3 or less
      if (this.nextLevel <= 3 && this.newTableType === 'WEEKLY') {
        const weeklyTypeTitle = `WEEKLY_N${this.nextLevel}`;
        const weeklyType = this.originalTableTypes.find(type => type.title === weeklyTypeTitle);
        return weeklyType ? weeklyType.priceMxn : 0;
      }

      // Weekly plus tables
      if (this.newTableType === 'WEEKLY' && this.lostWeightKg <= -1) return this.originalTableTypes.find(type => type.title === 'WEEKLY_PLUS')?.priceMxn || 0;

      // Weekly failed tables
      if (this.isClassCFailed) return this.originalTableTypes.find(type => type.title === 'WEEKLY_FAILED')?.priceMxn || 0;

      // Other table types
      const selectedType = this.originalTableTypes.find(type => type.title === this.newTableType);
      return selectedType ? selectedType.priceMxn : 0;
    },
    selectedTableTypeLabel(): string {
      const selectedType = this.originalTableTypes.find(type => type.title === this.newTableType);
      return selectedType ? selectedType.label : '';
    },
    selectedTableTypeDurationDays(): number {
      const selectedType = this.originalTableTypes.find(type => type.title === this.newTableType);
      return selectedType ? selectedType.durationDays : 0;
    },
    isClassCFailed(): boolean {
      if (!this.consultationWeightKg) return false;
      return this.client.currentClass === 'C' && this.lostWeightKg >= 0;
    },
    appointmentDateTime(): string | null {
      if (!this.selectedDate || !this.selectedTime) return null;
      return `${this.selectedDate}T${this.selectedTime}:00`;
    },
    formattedAppointmentDateTime(): string | null {
      if (!this.appointmentDateTime) return null;
      const date = new Date(this.appointmentDateTime);
      const options: Intl.DateTimeFormatOptions = {
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      };
      return new Intl.DateTimeFormat('es-MX', options).format(date);
    },
    minimumDate(): string {
      const min = new Date();
      min.setHours(0, 0, 0, 0);
      min.setDate(min.getDate() + this.selectedTableTypeDurationDays);
      return this.formatLocalToISO(min);
    },
    maximumDate(): string {
      const max = new Date();
      max.setHours(0, 0, 0, 0);
      max.setDate(max.getDate() + this.selectedTableTypeDurationDays + 3);
      return this.formatLocalToISO(max);
    },
    isTransitionTable(): boolean {
      if (this.nextLevel < 9) return false;
      return (this.nextLevel - 9) % 8 === 0;
    },
    isMaintenanceTable(): boolean {
      return this.newTableType === 'MAINTENANCE' || this.newTableType === 'CONTROL';
    },
    isValidForm(): boolean {
      return !this.consultationWeightKg ||
          !this.newTableType ||
          (this.isMaintenanceTable ? false : !this.appointmentDateTime);
    },
    hasEatenLunchOrDinner(): boolean {
      return (this.foodRemovalTime === 'LUNCH' ||
          this.foodRemovalTime === 'EVENING_SNACK' ||
          this.foodRemovalTime === 'DINNER')
    }
  },
  methods: {
    async getClientDetails() {
      this.isLoading = true;
      const clientId = this.$route.params.id;
      try {
        const response = await ClientService.getClientConsultationInfo(clientId);
        this.client = response.client;
        this.previousQuantityTable = response.quantityTable || null;
        if (!this.previousQuantityTable) return;
        this.morningSnackGrams = this.previousQuantityTable.morningSnack;
        this.eveningSnackGrams = this.previousQuantityTable.eveningSnack;
        if (this.previousQuantityTable.type === 'ABC') {
          Swal.fire({
            icon: 'info',
            titleText: 'Aviso importante',
            text: 'Entrega al cliente las indicaciones a seguir después de una tabla ABC.',
            timer: 5000,
            showConfirmButton: false,
            showCancelButton: false,
            allowEscapeKey: true,
            allowOutsideClick: false,
            background: '#fff',
            color: '#000',
            iconColor: '#ff6600',
            timerProgressBar: true,
            position: 'center',
            target: '.v-application'
          }).then(r => r)
        }
      } finally {
        this.isLoading = false;
      }
    },
    async updateClientNotes() {
      this.isLoadingClientNotes = true;
      try {
        const body = {
          idClient: this.client.idClient,
          notes: this.client.notes,
        }
        await ClientService.updateClientNotes(body);
        throwSuccessMessage("Notas actualizadas correctamente.");
      } finally {
        this.isLoadingClientNotes = false;
      }
    },
    async createNewQuantityTable() {
      const form = this.$refs.consultationForm as any;
      if (!form.validate()) {
        throwErrorMessage("Por favor, corrige los errores en el formulario antes de generar la tabla de cantidades.");
        return;
      }
      this.isLoading = true;
      try {
        const body = {
          clientId: this.client.idClient,
          breakfastPercentage: this.client.currentBreakfastPercentage,
          lunchPercentage: this.client.currentLunchPercentage,
          dinnerPercentage: this.client.currentDinnerPercentage,
          tableTypeTitle: this.newTableType,
          level: this.nextLevel,
          tableClass: this.nextClass,
          lostWeightKg: this.lostWeightKg,
          morningSnack: this.morningSnackGrams,
          eveningSnack: this.eveningSnackGrams,
          decreaseFoodGrams: this.decreaseFoodGrams ? this.foodGramsToDecrease : 0,
          isClassCFailed: this.isClassCFailed,
          nextAppointment: this.isMaintenanceTable ? null : this.appointmentDateTime,
          hasEatenLunchOrDinner: this.hasEatenLunchOrDinner,
        } as NewQuantityTable;
        this.newQuantityTable = await QuantityTableServices.updateQuantityTable(body);
        await this.$store.dispatch('balance/updateBalance')
        await this.$store.dispatch('balance/updateRoyalties', body);
        this.openDialogNewQuantityTable();
      } finally {
        this.isLoading = false;
      }
    },
    async getQuantityTableTypes() {
      this.isLoading = true;
      try {
        this.originalTableTypes = await QuantityTableServices.getQuantityTableTypes();
      } finally {
        this.isLoading = false;
      }
    },
    onNotesInput() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.updateClientNotes();
      }, 1500);
    },
    preventNonNumeric(event: KeyboardEvent) {
      if (['e', 'E', '+', '-'].includes(event.key)) {
        event.preventDefault();
      }
    },
    updateConsultationWeight() {
      if (this.decreaseFoodGrams) {
        this.consultationWeightKg = parseFloat((this.client.currentWeightKg - (this.foodGramsToDecrease / 1000)).toFixed(3));
      }
    },
    openDialogNewQuantityTable() {
      this.isNewQuantityTableDialogVisible = true;
    },
    formatLocalToISO(date: Date): string {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    }
  },
  watch: {
    lostWeightKg() {
      if (this.consultationWeightKg < 25) return; // prevent message while typing weight
      if (this.isTransitionTable) {
        const snacks = snackCalculator(this.BMI);
        if (this.morningSnackGrams === snacks[0] &&
            this.eveningSnackGrams === snacks[1]) {
          return;
        }
        this.morningSnackGrams = snacks[0];
        this.eveningSnackGrams = snacks[1];
        throwSuccessMessage("Se actualizaron los snacks automáticamente debido a que el cliente se encuentra en un nivel de transición.");
      } else {
        if (!this.previousQuantityTable) return;
        this.morningSnackGrams = this.previousQuantityTable.morningSnack;
        this.eveningSnackGrams = this.previousQuantityTable.eveningSnack;
      }
    },
    newTableType() {
      this.selectedDate = null;
      this.selectedTime = null;
    },
  },
  mounted() {
    this.getClientDetails();
    this.getQuantityTableTypes();
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
});
</script>

<style scoped>

</style>